// src/views/HomeView.js

import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { db } from '../firebase/config';
import { collection, query, where, onSnapshot, orderBy, getDocs, doc, updateDoc, getDoc, limit } from 'firebase/firestore';
import { 
    Target, FileText, Briefcase, FilePlus2, ChevronRight, PhoneCall,
    AlertCircle, Clock, Calendar, MapPin, CheckCircle, TrendingUp, DollarSign,
    ChevronDown, ChevronUp, X
} from 'lucide-react';
import { format, differenceInMinutes, formatDistanceToNow, isToday } from 'date-fns';
import { formatCurrency } from '../utils/formatters';
// PHASE 1A VERIFIER: r1-p1a-homeview-6e9c1b34
// Selective-approval feature: status tolerance helpers. Replaces hardcoded
// `status === 'Approved' || status === 'Accepted'` and fixes
// `'Changes Requested'` (with space) which never matched canonical
// `'ChangesRequested'`. Once Phase 2 ships, the CF will start writing
// `'ChangesRequested'` and this counter will start populating correctly.
import { isApprovedStatus, isChangesRequestedStatus, DOC_TYPES, resolveDocTerminology, isQuoteArchived } from '../utils/quoteSchema'; // r1-p1-docterm-home-3e8b1f04; isQuoteArchived: estdel-f9ac8ba8

// r1-p1-docterm-home-3e8b1f04
// Live org-default document terminology (Quotes/Estimates) for aggregate labels.
// Reads quoteTerminologyDefault off the org doc — same path/shape CrmApp uses.
// Missing org / loading / absent field → DOC_TYPES.QUOTE → "Quotes". Shared by the
// Sales and Admin dashboards so each is one call, no companySettings prop thread.
const useQuoteTerminologyDefault = (currentUserProfile) => {
    const [term, setTerm] = useState(DOC_TYPES.QUOTE);
    useEffect(() => {
        const orgId = currentUserProfile?.organizationId;
        if (!orgId) return;
        const unsub = onSnapshot(doc(db, `organizations/${orgId}`), (d) => {
            if (d.exists()) setTerm(d.data()?.quoteTerminologyDefault || DOC_TYPES.QUOTE);
        });
        return () => unsub();
    }, [currentUserProfile?.organizationId]);
    return resolveDocTerminology(term);
};

// --- SHARED COMPONENTS ---

// 1. Time Tracking Widget (Updated for Mobile Accessibility)
const TimeTrackingWidget = ({ activeShift, onClockIn, onClockOut, busy }) => { // clockbusy-e3b6c8a4
    const [timeElapsed, setTimeElapsed] = useState('');

    useEffect(() => {
        let timer;
        if (activeShift?.shiftStart) {
            const startTime = activeShift.shiftStart.toDate();
            const updateTime = () => {
                const now = new Date();
                const minutes = differenceInMinutes(now, startTime);
                const hours = Math.floor(minutes / 60);
                const remainingMinutes = minutes % 60;
                setTimeElapsed(`${hours}h ${remainingMinutes}m`);
            };
            updateTime();
            timer = setInterval(updateTime, 60000);
        } else {
            setTimeElapsed('Ready to clock in');
        }
        return () => { if (timer) clearInterval(timer); };
    }, [activeShift]);

    const isClockedIn = !!activeShift;

    return (
        <div className="bg-white p-4 rounded-lg shadow border border-transparent mb-6">
            <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                <Clock size={16} className={isClockedIn ? "text-green-500" : "text-gray-400"} />
                Time Tracking
            </h3>
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                <div className={isClockedIn ? "text-green-600 font-semibold text-lg sm:text-base" : "text-gray-500 text-sm"}>
                    {isClockedIn ? `Clocked In: ${timeElapsed}` : 'Status: Clocked Out'}
                </div>
                <button
                    onClick={isClockedIn ? onClockOut : onClockIn}
                    disabled={busy}
                    className={`w-full sm:w-auto px-6 py-4 sm:py-2 text-lg sm:text-sm font-bold rounded-xl sm:rounded-full transition-all shadow-md active:scale-95 flex justify-center items-center gap-2 ${
                        busy ? 'opacity-50 cursor-not-allowed' : '' /* clockbusy-e3b6c8a4 */
                    } ${
                        isClockedIn
                        ? 'bg-red-500 hover:bg-red-600 text-white'
                        : 'bg-green-500 hover:bg-green-600 text-white'
                    }`}
                >
                    {isClockedIn ? 'Clock Out' : 'Clock In'}
                </button>
            </div>
        </div>
    );
};

// 2. Overdue Events Widget
const OverdueWidget = ({ events, openJobEditorModal }) => (
    <div className="bg-white p-4 rounded-lg shadow h-fit">
        <div className="flex justify-between items-center mb-4">
            <h3 className="font-bold text-gray-800">My Overdue Events</h3>
            <span className="text-xs font-semibold bg-red-100 text-red-600 px-2 py-0.5 rounded-full">
                {events.length}
            </span>
        </div>
        {events.length > 0 ? (
            <div className="space-y-3">
                {events.map(event => (
                    <div 
                        key={event.id} 
                        onClick={() => openJobEditorModal(event)}
                        className="block p-3 bg-red-50 rounded-lg hover:bg-red-100 border border-red-200 cursor-pointer transition-colors"
                    >
                        <div className="flex justify-between items-center">
                            <p className="font-semibold text-sm text-red-800">{event.title}</p>
                            <AlertCircle size={16} className="text-red-500" />
                        </div>
                        <p className="text-xs text-red-600 mt-1">
                            {event.startAt?.toDate ? formatDistanceToNow(event.startAt.toDate(), { addSuffix: true }) : 'Unknown date'}
                        </p>
                    </div>
                ))}
            </div>
        ) : (
            <p className="text-sm text-gray-500 text-center py-4">Nothing overdue. Great job! ✅</p>
        )}
    </div>
);

// 3. Workflow Item (With Deep Link Support)
const WorkflowItem = ({ title, count, subtotals = [], icon, color, to }) => (
    <Link to={to} className="bg-white p-3 rounded-lg shadow hover:shadow-md transition-shadow cursor-pointer border border-transparent hover:border-gray-200 block min-w-0">
        <div className="flex items-center gap-2">
            <div className={`p-1.5 rounded-full shrink-0 ${color.bg}`}>
                {React.cloneElement(icon, { size: 18, className: color.text })}
            </div>
            <div className="min-w-0">
                <p className="text-xs text-gray-500 leading-tight truncate">{title}</p>
                <p className="text-xl font-bold leading-tight">{count}</p>
            </div>
        </div>
        {subtotals.length > 0 && (
            <div className="mt-2 text-xs text-gray-500 space-y-0.5 border-t border-gray-100 pt-2">
                {subtotals.map(sub => (
                    <div key={sub.label} className="flex justify-between gap-1">
                        <span className="truncate">{sub.label}</span>
                        <span className="font-semibold shrink-0">{sub.count}</span>
                    </div>
                ))}
            </div>
        )}
    </Link>
);

// ============================================================================
// GETTING STARTED WIDGET
// The industry-best onboarding checklist — shown only to Owners on first login.
// Steps are detected from real Firestore data, not localStorage.
// Starts at 20% progress (account creation is pre-checked).
// Permanently dismissed by writing onboardingCompleted: true to Firestore.
// ============================================================================
const GettingStartedWidget = ({ currentUserProfile }) => {
    const [steps, setSteps] = useState([
        { id: 'account',  label: 'Create your account',       description: 'You\'re in. Welcome to Spatchy.',           done: true,  to: null },
        { id: 'customer', label: 'Add your first customer',   description: 'Your customer list is the foundation.',     done: false, to: '/customers' },
        { id: 'job',      label: 'Schedule your first job',   description: 'Put a job on the calendar.',                done: false, to: '/calendar' },
        { id: 'quote',    label: 'Send your first estimate', description: 'Get your first estimate out the door.', done: false, to: '/quotes' }, // r1-p1-docterm-home-3e8b1f04
        { id: 'profile',  label: 'Set up your company brand', description: 'Add your logo and company details.',        done: false, to: '/settings?tab=companyInfo' },
    ]);
    const [collapsed, setCollapsed] = useState(false);
    const [dismissed, setDismissed] = useState(false);
    const [loading, setLoading] = useState(true);

    const orgId = currentUserProfile?.organizationId;
    const userId = currentUserProfile?.id;

    // Check if already completed in Firestore
    useEffect(() => {
        if (!userId) return;
        if (currentUserProfile?.onboardingCompleted) {
            setDismissed(true);
        }
    }, [currentUserProfile, userId]);

    // Detect completion of each step from real Firestore data
    useEffect(() => {
        if (!orgId || dismissed) return;

        const checkSteps = async () => {
            try {
                // Check customers
                const customersSnap = await getDocs(
                    query(collection(db, `organizations/${orgId}/customers`), limit(1))
                );
                const hasCustomer = !customersSnap.empty;

                // Check jobs
                const jobsSnap = await getDocs(
                    query(collection(db, `organizations/${orgId}/jobs`), limit(1))
                );
                const hasJob = !jobsSnap.empty;

                // Check sent quotes
                const quotesSnap = await getDocs(
                    query(collection(db, `organizations/${orgId}/quotes`), where('status', '==', 'Sent'), limit(1))
                );
                const hasSentQuote = !quotesSnap.empty;

                // Check company profile (logo + companyName)
                const orgSnap = await getDoc(doc(db, `organizations/${orgId}`));
                const orgData = orgSnap.data() || {};
                const hasProfile = !!(orgData.companyName && orgData.logo);

                setSteps(prev => prev.map(step => {
                    if (step.id === 'customer') return { ...step, done: hasCustomer };
                    if (step.id === 'job')      return { ...step, done: hasJob };
                    if (step.id === 'quote')    return { ...step, done: hasSentQuote };
                    if (step.id === 'profile')  return { ...step, done: hasProfile };
                    return step;
                }));
            } catch (err) {
                console.error('GettingStarted check error:', err);
            } finally {
                setLoading(false);
            }
        };

        checkSteps();
    }, [orgId, dismissed]);

    // Auto-dismiss when all steps complete
    useEffect(() => {
        if (loading) return;
        const allDone = steps.every(s => s.done);
        if (allDone) {
            handleDismiss();
        }
    }, [steps, loading]);

    const handleDismiss = async () => {
        setDismissed(true);
        if (!userId) return;
        try {
            await updateDoc(doc(db, 'users', userId), { onboardingCompleted: true });
        } catch (err) {
            console.error('Failed to save onboarding completion:', err);
        }
    };

    if (dismissed) return null;

    const completedCount = steps.filter(s => s.done).length;
    const totalCount = steps.length;
    const progressPct = Math.round((completedCount / totalCount) * 100);

    return (
        <div className="bg-white rounded-xl border border-blue-200 shadow-sm mb-8 overflow-hidden">
            {/* Header */}
            <div
                className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-blue-50 transition-colors"
                onClick={() => setCollapsed(p => !p)}
            >
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center shrink-0">
                        <CheckCircle size={16} className="text-white" />
                    </div>
                    <div>
                        <h3 className="font-bold text-gray-800 text-sm">Get Started with Spatchy</h3>
                        <p className="text-xs text-gray-500 mt-0.5">{completedCount} of {totalCount} steps complete</p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    {/* Progress bar */}
                    <div className="hidden sm:flex items-center gap-2">
                        <div className="w-32 h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div
                                className="h-full bg-blue-600 rounded-full transition-all duration-500"
                                style={{ width: `${progressPct}%` }}
                            />
                        </div>
                        <span className="text-xs font-semibold text-blue-600">{progressPct}%</span>
                    </div>
                    {collapsed ? <ChevronDown size={18} className="text-gray-400" /> : <ChevronUp size={18} className="text-gray-400" />}
                    <button
                        onClick={(e) => { e.stopPropagation(); handleDismiss(); }}
                        className="p-1 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-600 transition-colors"
                        title="Dismiss"
                    >
                        <X size={16} />
                    </button>
                </div>
            </div>

            {/* Progress bar (mobile) */}
            {!collapsed && (
                <div className="sm:hidden px-5 pb-2">
                    <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-blue-600 rounded-full transition-all duration-500"
                            style={{ width: `${progressPct}%` }}
                        />
                    </div>
                </div>
            )}

            {/* Steps */}
            {!collapsed && (
                <div className="px-5 pb-5 space-y-2">
                    {steps.map((step, index) => (
                        <div key={step.id}>
                            {step.done ? (
                                // Completed step — still clickable as a refresher
                                step.to ? (
                                    <Link
                                        to={step.to}
                                        className="flex items-center gap-3 p-3 rounded-lg bg-green-50 border border-green-100 hover:bg-green-100 transition-colors group"
                                    >
                                        <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center shrink-0">
                                            <CheckCircle size={14} className="text-white" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-green-800 line-through">{step.label}</p>
                                        </div>
                                        <ChevronRight size={14} className="text-green-400 opacity-0 group-hover:opacity-100 shrink-0 transition-opacity" />
                                    </Link>
                                ) : (
                                    <div className="flex items-center gap-3 p-3 rounded-lg bg-green-50 border border-green-100">
                                        <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center shrink-0">
                                            <CheckCircle size={14} className="text-white" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-green-800 line-through">{step.label}</p>
                                        </div>
                                    </div>
                                )
                            ) : step.to ? (
                                // Incomplete step — clickable link
                                <Link
                                    to={step.to}
                                    className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all group"
                                >
                                    <div className="w-6 h-6 rounded-full border-2 border-gray-300 group-hover:border-blue-500 flex items-center justify-center shrink-0 transition-colors">
                                        <span className="text-xs font-bold text-gray-400 group-hover:text-blue-500">{index + 1}</span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-semibold text-gray-800 group-hover:text-blue-700">{step.label}</p>
                                        <p className="text-xs text-gray-500 mt-0.5">{step.description}</p>
                                    </div>
                                    <ChevronRight size={16} className="text-gray-300 group-hover:text-blue-500 shrink-0 transition-colors" />
                                </Link>
                            ) : null}
                        </div>
                    ))}

                    {/* Dismiss link */}
                    <div className="pt-2 text-center">
                        <button
                            onClick={handleDismiss}
                            className="text-xs text-gray-400 hover:text-gray-600 underline transition-colors"
                        >
                            I'm all set — hide this
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

// ============================================================================
// DASHBOARD 1: FIELD TECHNICIAN
// ============================================================================
const TechDashboard = ({ currentUserProfile, activeShift, onClockIn, onClockOut, openJobEditorModal, clockBusy }) => { // clockbusy-e3b6c8a4
    const [todaysJobs, setTodaysJobs] = useState([]);
    const [overdueJobs, setOverdueJobs] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const orgId = currentUserProfile.organizationId;
        const now = new Date();
        const startOfDay = new Date(); startOfDay.setHours(0,0,0,0);
        const endOfDay = new Date(); endOfDay.setHours(23,59,59,999);

        const jobsRef = collection(db, `organizations/${orgId}/jobs`);
        const q = query(
            jobsRef,
            where('assignedTo', 'array-contains', currentUserProfile.id),
            where("status", "!=", "Canceled")
        );

        const unsub = onSnapshot(q, (snap) => {
            const allMyJobs = snap.docs.map(d => ({ ...d.data(), id: d.id })); // s6costing-docid: doc id wins over a stale stored `id` field
            
            const today = allMyJobs.filter(j => {
                const start = j.startAt?.toDate();
                return start && start >= startOfDay && start <= endOfDay
                    && j.status !== 'Completed' && j.status !== 'Canceled';
            }).sort((a,b) => a.startAt.toDate() - b.startAt.toDate());

            const overdue = allMyJobs.filter(j => {
                const start = j.startAt?.toDate();
                return start && start < now && j.status !== 'Completed';
            }).sort((a,b) => a.startAt.toDate() - b.startAt.toDate());

            setTodaysJobs(today);
            setOverdueJobs(overdue);
            setLoading(false);
        });
        return () => unsub();
    }, [currentUserProfile]);

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
                <TimeTrackingWidget activeShift={activeShift} onClockIn={onClockIn} onClockOut={onClockOut} busy={clockBusy} />
                
                <h2 className="text-lg font-bold text-gray-700 mb-4 flex items-center gap-2">
                    <Calendar size={20} /> Today's Schedule
                </h2>
                
                <div className="space-y-4">
                    {loading ? (
                        <p className="text-gray-400 italic">Loading schedule...</p>
                    ) : todaysJobs.length === 0 ? (
                        <div className="bg-white border border-gray-200 rounded-lg p-8 text-center shadow-sm">
                            <CheckCircle size={48} className="mx-auto text-green-400 mb-3" />
                            <p className="text-gray-600 font-medium">No jobs scheduled for today.</p>
                        </div>
                    ) : (
                        todaysJobs.map(job => (
                            <div key={job.id} className="bg-white border rounded-lg p-5 shadow-sm hover:shadow-md transition-shadow">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="bg-blue-100 text-blue-700 text-xs font-bold px-2 py-0.5 rounded">
                                                {format(job.startAt.toDate(), 'h:mm a')}
                                            </span>
                                            <h3 className="font-bold text-gray-800">{job.title}</h3>
                                        </div>
                                        <p className="text-gray-600 text-sm flex items-center gap-1 mt-1">
                                            <MapPin size={14} /> {job.location || "No location provided"}
                                        </p>
                                    </div>
                                    <button 
                                        onClick={() => openJobEditorModal(job)}
                                        className="text-blue-600 hover:bg-blue-50 px-3 py-1.5 rounded-md text-sm font-medium transition-colors"
                                    >
                                        View
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
            <div>
                <OverdueWidget events={overdueJobs} openJobEditorModal={openJobEditorModal} />
            </div>
        </div>
    );
};

// ============================================================================
// DASHBOARD 2: SALES CONSULTANT
// ============================================================================
const SalesDashboard = ({ currentUserProfile, openJobEditorModal, activeShift, onClockIn, onClockOut, clockBusy }) => { // clockbusy-e3b6c8a4
    const { wordPlural: docWordPlural } = useQuoteTerminologyDefault(currentUserProfile); // r1-p1-docterm-home-3e8b1f04
    const [stats, setStats] = useState({ leads: 0, drafts: 0, pending: 0 });
    const [overdueJobs, setOverdueJobs] = useState([]);

    useEffect(() => {
        const orgId = currentUserProfile.organizationId;
        const leadsRef = collection(db, `organizations/${orgId}/requests`);
        const leadsQ = query(leadsRef, where('status', '==', 'New'));
        
        const unsubLeads = onSnapshot(leadsQ, (snap) => {
            const myLeads = snap.docs.filter(doc => {
                const data = doc.data();
                const assignedToMe = data.assignedTo === currentUserProfile.id;
                const isUnassigned = !data.assignedTo;
                return assignedToMe || isUnassigned;
            });
            setStats(prev => ({ ...prev, leads: myLeads.length }));
        });

        const quotesQ = query(collection(db, `organizations/${orgId}/quotes`), where('salesPersonId', '==', currentUserProfile.id));
        const unsubQuotes = onSnapshot(quotesQ, (snap) => {
            const live = snap.docs.filter(d => !isQuoteArchived(d.data())); // estdel-f9ac8ba8 — deleted estimates are not counted
            const drafts = live.filter(d => d.data().status === 'Draft').length;
            const pending = live.filter(d => d.data().status === 'Sent').length;
            setStats(prev => ({ ...prev, drafts, pending }));
        });

        const now = new Date();
        const jobsRef = collection(db, `organizations/${orgId}/jobs`);
        const qJobs = query(jobsRef, where('assignedTo', 'array-contains', currentUserProfile.id), where("status", "!=", "Canceled"));
        
        const unsubJobs = onSnapshot(qJobs, (snap) => {
            const overdue = snap.docs
                .map(d => ({ ...d.data(), id: d.id })) // s6costing-docid: doc id wins over a stale stored `id` field
                .filter(j => j.startAt?.toDate() < now && j.status !== 'Completed')
                .sort((a,b) => a.startAt.toDate() - b.startAt.toDate());
            setOverdueJobs(overdue);
        });

        return () => { unsubLeads(); unsubQuotes(); unsubJobs(); };
    }, [currentUserProfile]);

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
                <TimeTrackingWidget activeShift={activeShift} onClockIn={onClockIn} onClockOut={onClockOut} busy={clockBusy} />
                
                <h2 className="text-lg font-semibold text-gray-700 mb-4">My Pipeline</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <WorkflowItem 
                        title="New Leads" 
                        count={stats.leads} 
                        icon={<Target />} 
                        color={{bg: 'bg-orange-100', text: 'text-orange-600'}} 
                        to="/leads" 
                    />
                    <WorkflowItem 
                        title={`Draft ${docWordPlural}`} 
                        count={stats.drafts} 
                        icon={<FileText />} 
                        color={{bg: 'bg-gray-100', text: 'text-gray-600'}} 
                        to="/quotes" 
                    />
                    <WorkflowItem 
                        title="Pending Approval" 
                        count={stats.pending} 
                        icon={<TrendingUp />} 
                        color={{bg: 'bg-blue-100', text: 'text-blue-600'}} 
                        to="/quotes"
                    />
                </div>
                {/* qreport-5b8e2c64 (Quote Seam 12a) — a consultant has no Reports
                    hub (no VIEW_REPORTS), so this is their way in. The report
                    narrows itself to estimates where they are the salesperson. */}
                <Link
                    to="/reports/estimates"
                    className="mt-4 flex items-center justify-between bg-white border rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow"
                >
                    <span className="flex items-center gap-3 font-semibold text-gray-800">
                        <TrendingUp className="text-green-600" size={20} /> My {docWordPlural.toLowerCase()} report
                    </span>
                    <span className="text-sm text-gray-500 flex items-center gap-1">Win rate &amp; why deals closed <ChevronRight size={16} /></span>
                </Link>
            </div>
            <div>
                <OverdueWidget events={overdueJobs} openJobEditorModal={openJobEditorModal} />
            </div>
        </div>
    );
};

// ============================================================================
// DASHBOARD 3: ADMIN / MANAGER / OWNER
// ============================================================================
const AdminDashboard = ({ currentUserProfile, activeShift, onClockIn, onClockOut, openJobEditorModal, clockBusy }) => { // clockbusy-e3b6c8a4
    const { wordPlural: docWordPlural } = useQuoteTerminologyDefault(currentUserProfile); // r1-p1-docterm-home-3e8b1f04
    const [counts, setCounts] = useState({ 
        leads: 0, approvedQuotes: 0, draftQuotes: 0, changesQuotes: 0, 
        requiresInvoicing: 0, activeJobs: 0, pendingJobs: 0,
        awaitingPayment: 0, unpaidInvoices: 0, draftInvoices: 0, totalReceivables: 0 
    });
    const [overdueEvents, setOverdueEvents] = useState([]);

    useEffect(() => {
        const orgId = currentUserProfile.organizationId;
        const unsub1 = onSnapshot(query(collection(db, `organizations/${orgId}/requests`), where('status', '==', 'New')), 
            s => setCounts(p => ({ ...p, leads: s.size })));

        const unsub2 = onSnapshot(collection(db, `organizations/${orgId}/quotes`), s => {
            const live = s.docs.filter(d => !isQuoteArchived(d.data())); // estdel-f9ac8ba8 — deleted estimates are not counted
            // CONVERT-FIX: converted quotes (marker: convertedToJobId) are done — not "ready".
            const approved = live.filter(d => isApprovedStatus(d.data().status) && !d.data().convertedToJobId).length;
            const drafts = live.filter(d => d.data().status === 'Draft').length;
            const changes = live.filter(d => isChangesRequestedStatus(d.data().status)).length;
            setCounts(p => ({ ...p, approvedQuotes: approved, draftQuotes: drafts, changesQuotes: changes }));
        });

        const unsub3 = onSnapshot(query(collection(db, `organizations/${orgId}/jobs`), where("status", "!=", "Canceled")), s => {
            const docs = s.docs.map(d => ({ ...d.data(), id: d.id })); // s6costing-docid: doc id wins over a stale stored `id` field
            const reqInv = docs.filter(j => j.status === 'Completed' && !j.invoiceId).length;
            const active = docs.filter(j => j.status === 'In Progress').length;
            const pending = docs.filter(j => j.status === 'Pending').length;
            const now = new Date();
            const overdue = docs.filter(j => j.startAt?.toDate() < now && j.status !== 'Completed' && j.assignedTo?.includes(currentUserProfile.id));
            setCounts(p => ({ ...p, requiresInvoicing: reqInv, activeJobs: active, pendingJobs: pending }));
            setOverdueEvents(overdue);
        });

        const unsub4 = onSnapshot(collection(db, `organizations/${orgId}/invoices`), s => {
            const docs = s.docs.map(d => ({ ...d.data(), id: d.id })); // s6costing-docid: doc id wins over a stale stored `id` field
            const unpaidList = docs.filter(i => ['Sent', 'Past Due', 'Unpaid', 'Partial'].includes(i.status));
            const drafts = docs.filter(i => i.status === 'Draft').length;
            const totalRec = unpaidList.reduce((sum, i) => sum + (parseFloat(i.balanceDue !== undefined ? i.balanceDue : i.total) || 0), 0);
            setCounts(p => ({ ...p, awaitingPayment: unpaidList.length, unpaidInvoices: unpaidList.length, draftInvoices: drafts, totalReceivables: totalRec }));
        });

        return () => { unsub1(); unsub2(); unsub3(); unsub4(); };
    }, [currentUserProfile]);

    const isOwner = ['Owner', 'Owner/System Admin', 'System Admin', 'Admin'].includes(currentUserProfile?.role);

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">

                {/* ── GETTING STARTED WIDGET ─────────────────────────── */}
                {isOwner && !currentUserProfile?.onboardingCompleted && (
                    <GettingStartedWidget currentUserProfile={currentUserProfile} />
                )}
                {/* ─────────────────────────────────────────────────── */}

                <TimeTrackingWidget activeShift={activeShift} onClockIn={onClockIn} onClockOut={onClockOut} busy={clockBusy} />
                
                <h2 className="text-lg font-semibold text-gray-700 mb-4 mt-8">Workflow</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
                    <WorkflowItem 
                        to="/jobs?filter=NeedsConfirmation" 
                        title="Needs Confirmation" 
                        count={counts.pendingJobs} 
                        icon={<PhoneCall />} 
                        color={{bg: 'bg-red-100', text: 'text-red-600'}} 
                    />
                    <WorkflowItem 
                        to="/leads" 
                        title="New Leads" 
                        count={counts.leads} 
                        icon={<Target />} 
                        color={{bg: 'bg-orange-100', text: 'text-orange-600'}} 
                    />
                    <WorkflowItem 
                        to="/quotes?filter=Approved" 
                        title={`Approved ${docWordPlural}`} 
                        count={counts.approvedQuotes} 
                        subtotals={[
                            { label: 'Draft', count: counts.draftQuotes },
                            { label: 'Changes Req', count: counts.changesQuotes },
                        ]}
                        icon={<FileText />} 
                        color={{bg: 'bg-purple-100', text: 'text-purple-600'}} 
                    />
                    <WorkflowItem 
                        to="/jobs?filter=RequiresInvoicing" 
                        title="Requires Invoicing" 
                        count={counts.requiresInvoicing} 
                        subtotals={[
                            { label: 'Active Jobs', count: counts.activeJobs },
                        ]}
                        icon={<Briefcase />} 
                        color={{bg: 'bg-green-100', text: 'text-green-600'}} 
                    />
                    <WorkflowItem 
                        to="/invoices?filter=Unpaid" 
                        title="Awaiting Payment" 
                        count={counts.awaitingPayment} 
                        subtotals={[
                            { label: 'Unpaid', count: counts.unpaidInvoices },
                            { label: 'Draft', count: counts.draftInvoices },
                        ]}
                        icon={<FilePlus2 />} 
                        color={{bg: 'bg-blue-100', text: 'text-blue-600'}} 
                    />
                </div>
            </div>

            <div className="space-y-6">
                <Link to="/reports/sales" className="block bg-white p-4 rounded-lg shadow border border-transparent hover:border-blue-300 hover:shadow-md transition-all cursor-pointer">
                    <div className="flex justify-between items-center mb-2">
                        <h3 className="font-bold text-gray-800">Business Performance</h3>
                        <div className="p-2 bg-green-50 rounded-full text-green-600">
                            <ChevronRight size={20} />
                        </div>
                    </div>
                    <div className="mt-4">
                        <p className="text-xs text-gray-500 uppercase tracking-wider">Total Receivables</p>
                        <h2 className="text-3xl font-bold text-green-600 mt-1">{formatCurrency(counts.totalReceivables)}</h2>
                        <p className="text-xs text-gray-400 mt-1">{counts.awaitingPayment} clients owe you</p>
                    </div>
                </Link>
                
                <OverdueWidget events={overdueEvents} openJobEditorModal={openJobEditorModal} />
                
                <div className="bg-white p-4 rounded-lg shadow text-center">
                    <h3 className="font-bold text-gray-800 mb-2">Upcoming Jobs</h3>
                    <p className="text-sm text-gray-400">No upcoming jobs this week.</p>
                </div>
            </div>
        </div>
    );
};

// ============================================================================
// MAIN CONTROLLER
// ============================================================================
const HomeView = (props) => {
    const role = props.currentUserProfile?.role;
    const greetingName = props.currentUserProfile?.name?.split(' ')[0] || 'there';
    const getGreeting = () => {
        const hour = new Date().getHours();
        if (hour < 12) return 'Good morning';
        if (hour < 17) return 'Good afternoon';
        return 'Good evening';
    };

    return (
        <div className="p-3 md:p-10 bg-gray-50 min-h-screen pb-24">
            <div className="mb-8 p-1">
                <p className="text-gray-500 text-sm">{format(new Date(), 'EEEE, MMMM d')}</p>
                <h1 className="text-3xl font-bold text-gray-800">{getGreeting()}, {greetingName}</h1>
            </div>

            {role === 'Field Technician' ? (
                <TechDashboard {...props} />
            ) : (role === 'Sales Consultant') ? (
                <SalesDashboard {...props} />
            ) : (
                <AdminDashboard {...props} />
            )}
        </div>
    );
};

export default HomeView;